import { FC, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { SOCIAL_ICONS } from 'src/constants/socialIcons'
import {
  MentionSentiment,
  MentionsRange,
  TopicDetail,
  axisMax,
  axisTicks,
} from 'src/interfaces/topics.interface'
import { ReviewCard } from 'src/charts/ReviewSentimentAnalysesSection/ReviewCard'
import { ReviewListItem } from 'src/services/reviewAnalysisApi/interfaces/reviewAnalysis.interface'
import styles from './topicDetailModal.module.scss'
import {
  MentionsPlot,
  PlatformBars,
  SentimentDonut,
  SubtopicBar,
  formatTimeAgo,
  languageName,
} from './parts'
import { DeltaBadge } from '../DeltaBadge'
import { SOCIAL_TOPICS_COPY, TopicsCopy, countLabel } from '../copy'

interface TopicDetailModalProps {
  // Wording for the product the modal sits in; Social Listening by default.
  copy?: TopicsCopy
  data?: TopicDetail
  isLoading?: boolean
  // Return from a subtopic to its topic (the header's back chevron).
  onBack: () => void
  onClose: () => void
  // Drill into a subtopic: the parent refetches the detail narrowed to it and
  // this same modal re-renders as "Topic › Subtopic".
  onOpenSubtopic: (name: string) => void
  // The Related Mentions tab; the parent refetches the posts narrowed to it.
  onSentimentChange: (sentiment: MentionSentiment | null) => void
  sentiment: MentionSentiment | null
  // The subtopic in view, null for the whole topic.
  subtopic: string | null
  // The topic name, known before the detail arrives, for the breadcrumb.
  topic: string | null
}

const COPY = {
  back: 'Back to topic',
  close: 'Close',
  subtopicTitle: 'Subtopic',
  title: 'Topic',
}

const RANGES: MentionsRange[] = ['7-Day', '14-Day', '30-Day']

// The Related Mentions tabs, in the design's order.
const SENTIMENT_TABS: { key: MentionSentiment | null; label: string }[] = [
  { key: null, label: 'All' },
  { key: 'POS', label: 'Positive' },
  { key: 'NEG', label: 'Negative' },
  { key: 'NEU', label: 'Neutral' },
]

const SENTIMENT_LABEL: Record<MentionSentiment, string> = {
  NEG: 'Negative',
  NEU: 'Neutral',
  POS: 'Positive',
}

const LEGEND_ITEMS = [
  { color: '#8080FF', key: 'positive', label: 'Positive' },
  { color: '#C7CED1', key: 'neutral', label: 'Neutral' },
  { color: '#FF6833', key: 'negative', label: 'Negative' },
]

export const TopicDetailModal: FC<TopicDetailModalProps> = ({
  copy = SOCIAL_TOPICS_COPY,
  data,
  isLoading = false,
  onBack,
  onClose,
  onOpenSubtopic,
  onSentimentChange,
  sentiment,
  subtopic,
  topic,
}) => {
  const subtopicMax = axisMax(data?.subtopics ?? [])
  const [range, setRange] = useState<MentionsRange>('7-Day')
  // The subtopic view is a fresh read, so `data` for the topic must not show
  // under the subtopic's title while it loads.
  const inSubtopic = !!subtopic
  // The query keeps the previous answer while the next one loads, so the
  // modal checks the data really describes the view it is showing.
  const dataMatches =
    !!data &&
    (data.subtopic ?? null) === subtopic &&
    (topic === null || (data.topic ?? data.name) === topic)
  // The tab's posts only: while the narrowed read is in flight the previous
  // answer may still hold posts of other sentiments.
  const shownMentions = dataMatches
    ? data!.relatedMentions.filter(
        (mention) => !sentiment || mention.sentiment === sentiment
      )
    : []

  // Escape closes, and the page behind must not scroll while the modal is open.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', onKeyDown)
    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [onClose])

  return createPortal(
    // Clicking the backdrop closes; clicks inside the card must not bubble out
    // to it or every interaction in the modal would shut it.
    <div
      className={styles.overlay}
      onClick={onClose}
      onKeyDown={undefined}
      role="presentation"
    >
      <div
        className={styles.modal}
        onClick={(event) => event.stopPropagation()}
        role="presentation"
      >
        <div className={styles.header}>
          {inSubtopic ? (
            // "‹ Topic › Subtopic": the chevron and the topic crumb both go
            // back up; the current crumb is the bold one.
            <h2 className={`${styles.title} ${styles.breadcrumb}`}>
              <button
                aria-label={COPY.back}
                className={styles.backButton}
                onClick={onBack}
                type="button"
              >
                <svg aria-hidden="true" fill="none" viewBox="0 0 16 16">
                  <path
                    d="M10 3.5L5.5 8L10 12.5"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="1.6"
                  />
                </svg>
              </button>
              <button
                className={styles.crumbMuted}
                onClick={onBack}
                type="button"
              >
                {COPY.title}
              </button>
              <span className={styles.crumbSep}>›</span>
              <span>{COPY.subtopicTitle}</span>
            </h2>
          ) : (
            <h2 className={styles.title}>{COPY.title}</h2>
          )}
          <button
            aria-label={COPY.close}
            className={styles.closeButton}
            onClick={onClose}
            type="button"
          >
            <svg aria-hidden="true" fill="none" viewBox="0 0 16 16">
              <path
                d="M3.5 3.5L12.5 12.5M12.5 3.5L3.5 12.5"
                stroke="currentColor"
                strokeLinecap="round"
                strokeWidth="1.6"
              />
            </svg>
          </button>
        </div>

        <div className={styles.splitter} />

        <div className={styles.body}>
          {/* The modal opens immediately and fills in: waiting ~850ms before
              anything appears reads as a dead click. */}
          {(isLoading || !dataMatches) && (
            <div className={styles.loading}>
              <span className={styles.loadingBlock} />
              <div className={styles.summaryRow}>
                <span
                  className={`${styles.summaryCard} ${styles.loadingBlock}`}
                />
                <span
                  className={`${styles.summaryCard} ${styles.loadingBlock}`}
                />
              </div>
              <span
                className={`${styles.loadingBlock} ${styles.loadingTall}`}
              />
            </div>
          )}
          {dataMatches && (
            <>
              <div className={styles.topicRow}>
                {inSubtopic && (
                  <span className={styles.topicParent} title={topic ?? ''}>
                    {data!.topic ?? topic}
                    <span className={styles.crumbSep}>›</span>
                  </span>
                )}
                <span className={styles.topicName}>{data!.name}</span>
                {/* No delta means no previous period to compare against — the
                pill would otherwise render as an empty capsule. */}
                {data!.delta !== null && (
                  <span
                    className={`${styles.topicDelta} ${
                      data!.delta < 0 ? styles.topicDeltaDown : ''
                    }`}
                  >
                    <DeltaBadge value={data!.delta} />
                  </span>
                )}
              </div>

              {!!data!.summary && (
                <p
                  className={`${styles.topicSummary} ${
                    copy.summaryNeutral ? styles.topicSummaryNeutral : ''
                  }`}
                >
                  {data!.summary}
                </p>
              )}

              <div className={styles.summaryRow}>
                <div className={styles.summaryCard}>
                  <SentimentDonut
                    mentions={data!.mentions}
                    noun={copy.noun}
                    nounSingular={copy.nounSingular}
                    sentiment={data!.sentiment}
                    shareLabel={inSubtopic ? 'of topic' : copy.shareOfTotal}
                    shareOfTotal={
                      inSubtopic
                        ? data!.shareOfTopic ?? data!.shareOfTotal
                        : data!.shareOfTotal
                    }
                  />
                </div>
                <div className={styles.summaryCard}>
                  <PlatformBars platforms={data!.platforms} />
                </div>
              </div>

              {/* A subtopic has no children: the drill-down (Figma frame 05)
                  goes straight from the summary to the posts behind it. */}
              {!inSubtopic && (
                <div className={styles.subtopics}>
                  <div className={styles.subHeader}>
                    <span className={styles.subTitle}>
                      Subtopics {data!.subtopics.length}{' '}
                      <span className={styles.subTitleMuted}>Identified</span>
                    </span>
                    <span className={styles.donutLegend}>
                      {LEGEND_ITEMS.map((item) => (
                        <span className={styles.legendItem} key={item.key}>
                          <span
                            className={styles.legendDot}
                            style={{ background: item.color }}
                          />
                          {item.label}
                        </span>
                      ))}
                    </span>
                  </div>

                  <div className={styles.subRows}>
                    {data!.subtopics.map((sub) => (
                      <div
                        className={styles.subRow}
                        key={sub.id}
                        onClick={() => onOpenSubtopic(sub.name)}
                        onKeyDown={(event) => {
                          if (
                            event.target === event.currentTarget &&
                            (event.key === 'Enter' || event.key === ' ')
                          ) {
                            event.preventDefault()
                            onOpenSubtopic(sub.name)
                          }
                        }}
                        role="button"
                        tabIndex={0}
                      >
                        <span>
                          <span className={styles.subName}>{sub.name}</span>
                          <span className={styles.subMeta}>
                            <strong>{countLabel(sub.mentions, copy)}</strong> ·{' '}
                            {sub.shareOfTopic}% of topic
                          </span>
                        </span>
                        <SubtopicBar
                          max={subtopicMax}
                          mentions={sub.mentions}
                          sentiment={sub.sentiment}
                          share={sub.shareOfTopic}
                        />
                        {sub.delta === null ? (
                          // Keeps the grid column, drops the empty capsule.
                          <span />
                        ) : (
                          <span
                            className={`${styles.subDelta} ${
                              sub.delta < 0 ? styles.subDeltaDown : ''
                            }`}
                          >
                            <DeltaBadge value={sub.delta} />
                          </span>
                        )}
                        <span className={styles.subChevron}>
                          <svg
                            aria-hidden="true"
                            fill="none"
                            viewBox="0 0 16 16"
                          >
                            <path
                              d="M6 3.5L10.5 8L6 12.5"
                              stroke="currentColor"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="1.6"
                            />
                          </svg>
                        </span>
                      </div>
                    ))}
                  </div>

                  {data!.subtopics.length === 0 && (
                    <span className={styles.cardEmpty}>
                      No subtopics have been identified for this topic yet
                    </span>
                  )}

                  {data!.subtopics.length > 0 && (
                    <div className={styles.subAxis}>
                      <span className={styles.subAxisTicks}>
                        {axisTicks(subtopicMax).map((tick) => (
                          <span className={styles.subAxisTick} key={tick}>
                            {tick}%
                          </span>
                        ))}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {!inSubtopic && (
                <div className={styles.mentions}>
                  <div className={styles.mentionsHeader}>
                    <span className={styles.mentionsLabel}>
                      <svg
                        aria-hidden="true"
                        className={styles.mentionsLabelMark}
                        fill="none"
                        viewBox="0 0 20 10"
                      >
                        <path
                          d="M1 5H17"
                          stroke="currentColor"
                          strokeWidth="1.5"
                        />
                        <circle
                          cx="9"
                          cy="5"
                          fill="#FFFFFF"
                          r="3"
                          stroke="currentColor"
                          strokeWidth="1.5"
                        />
                      </svg>
                      {copy.noun.charAt(0).toUpperCase() + copy.noun.slice(1)}
                    </span>
                    <span className={styles.rangeTabs}>
                      {RANGES.map((option) => (
                        <button
                          className={`${styles.rangeTab} ${
                            option === range ? styles.rangeTabActive : ''
                          }`}
                          key={option}
                          onClick={() => setRange(option)}
                          type="button"
                        >
                          {option}
                        </button>
                      ))}
                    </span>
                  </div>
                  <div className={styles.mentionsPlot}>
                    <MentionsPlot points={data!.trend[range] ?? []} />
                  </div>
                </div>
              )}

              <div className={styles.relatedHeader}>
                <span className={styles.relatedHeading}>
                  <span className={styles.relatedTitle}>
                    {copy.relatedTitle}
                  </span>
                  <span className={styles.relatedHint}>
                    {inSubtopic
                      ? copy.relatedHintSubtopic
                      : copy.relatedHintTopic}
                  </span>
                </span>
                <span className={styles.sentimentTabs}>
                  {SENTIMENT_TABS.map((tab) => (
                    <button
                      className={`${styles.sentimentTab} ${
                        tab.key === sentiment ? styles.sentimentTabActive : ''
                      }`}
                      key={tab.label}
                      onClick={() => onSentimentChange(tab.key)}
                      type="button"
                    >
                      {tab.label}
                    </button>
                  ))}
                </span>
              </div>
              <div className={styles.relatedList}>
                {shownMentions.length === 0 && (
                  <div className={styles.relatedEmpty}>
                    <svg aria-hidden="true" fill="none" viewBox="0 0 24 24">
                      <rect
                        height="16"
                        rx="3"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        width="18"
                        x="3"
                        y="4"
                      />
                      <path
                        d="M7 9H17M7 13H13"
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeWidth="1.5"
                      />
                    </svg>
                    <span className={styles.relatedEmptyTitle}>
                      {sentiment
                        ? `No ${SENTIMENT_LABEL[sentiment].toLowerCase()} ${copy.noun} here yet`
                        : inSubtopic
                          ? 'No posts linked to this subtopic yet'
                          : 'No posts linked to this topic yet'}
                    </span>
                    {!sentiment && (
                      <span className={styles.relatedEmptyHint}>
                        {copy.relatedEmptyHint}
                      </span>
                    )}
                  </div>
                )}
                {shownMentions.map((mention) => {
                  // A review (it has a star rating) is drawn exactly like the
                  // Sentiment Analysis review card: platform logo, location,
                  // link, stars and the Excellent / Good / Poor badge.
                  if (typeof mention.rating === 'number') {
                    return (
                      <ReviewCard
                        key={mention.id}
                        review={
                          {
                            _id: mention.id,
                            date: mention.publishedAt ?? '',
                            language: mention.language || undefined,
                            platform: mention.platform,
                            rating: mention.rating,
                            reviewerName: mention.reviewerName ?? undefined,
                            text: mention.text,
                            translated: mention.translated ?? undefined,
                            url: mention.url || undefined,
                            venueName: mention.venueName ?? '',
                          } as ReviewListItem
                        }
                        subtopic={inSubtopic ? null : mention.subtopic}
                      />
                    )
                  }
                  // The whole card opens the original post in a new tab; a
                  // mention without a link (some sources have none) stays a
                  // plain card rather than a dead anchor.
                  const href = mention.url || undefined
                  const Card = href ? 'a' : 'div'
                  return (
                    <Card
                      className={`${styles.mentionCard} ${href ? styles.mentionCardLink : ''}`}
                      key={mention.id}
                      {...(href
                        ? {
                            href,
                            rel: 'noopener noreferrer',
                            target: '_blank',
                            title: 'Open the original post',
                          }
                        : {})}
                    >
                      <div className={styles.mentionTop}>
                        <span className={styles.mentionPlatformIcon}>
                          {SOCIAL_ICONS[mention.platform] ?? SOCIAL_ICONS.earth}
                        </span>
                        <span className={styles.mentionPlatform}>
                          {mention.platform === 'twitter'
                            ? 'X (Twitter)'
                            : mention.platform.replace(/(^|\s)\S/g, (c) =>
                                c.toUpperCase()
                              )}
                        </span>
                        {typeof mention.rating === 'number' && (
                          <span className={styles.mentionRating}>
                            ★ {mention.rating}
                          </span>
                        )}
                        {!!mention.reviewerName && (
                          <span className={styles.mentionReviewer}>
                            {mention.reviewerName}
                          </span>
                        )}
                        <span
                          className={styles.mentionLink}
                          style={
                            mention.domain || mention.url
                              ? undefined
                              : { display: 'none' }
                          }
                        >
                          <svg
                            aria-hidden="true"
                            fill="none"
                            viewBox="0 0 14 14"
                          >
                            <path
                              d="M5.5 2.5H2.5V11.5H11.5V8.5M8 2.5H11.5V6M11.5 2.5L6.5 7.5"
                              stroke="currentColor"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="1.3"
                            />
                          </svg>
                          {mention.domain
                            ? mention.domain.charAt(0).toUpperCase() +
                              mention.domain.slice(1)
                            : copy.openLabel}
                        </span>
                        <span className={styles.mentionRight}>
                          {mention.sentiment && (
                            <span
                              className={`${styles.mentionSentiment} ${
                                styles[`mentionSentiment${mention.sentiment}`]
                              }`}
                            >
                              {SENTIMENT_LABEL[mention.sentiment]}
                            </span>
                          )}
                          <span
                            className={styles.mentionLanguage}
                            style={
                              mention.language ? undefined : { display: 'none' }
                            }
                          >
                            <svg
                              aria-hidden="true"
                              fill="none"
                              viewBox="0 0 14 14"
                            >
                              <circle
                                cx="7"
                                cy="7"
                                r="5.2"
                                stroke="currentColor"
                                strokeWidth="1.2"
                              />
                              <path
                                d="M1.8 7H12.2M7 1.8C8.5 3.5 8.5 10.5 7 12.2M7 1.8C5.5 3.5 5.5 10.5 7 12.2"
                                stroke="currentColor"
                                strokeWidth="1.2"
                              />
                            </svg>
                            {languageName(mention.language)}
                          </span>
                        </span>
                      </div>
                      <span className={styles.mentionTime}>
                        {formatTimeAgo(mention.publishedAt)}
                      </span>
                      <span className={styles.mentionText}>{mention.text}</span>
                    </Card>
                  )
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}
